import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import type { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { BaseDialog } from '@/components/shared/base-dialog';
import {
  courseTypesLabels,
  modalityTypesLabels,
} from '@/constants/catalog/course';
import { useNotification } from '@/context/notification';
import { useCreateAcademicSituation } from '@/data/learning/academic-situation';
import {
  CourseTypes,
  DurationTypes,
  DurationTypesLabels,
  ModalityTypes,
} from '@/models/catalog/course';
import { AcademicStatusType } from '@/models/learning/academic-situation';

const initialStatusOptions = [
  {
    value: AcademicStatusType.EnrollmentPending,
    label: 'Matrícula pendente',
  },
  {
    value: AcademicStatusType.InProgress,
    label: 'Em andamento',
  },
];

const categoryOptions = Object.values(CourseTypes).map((value) => ({
  value,
  label: courseTypesLabels[value],
}));

const modalityOptions = Object.values(ModalityTypes).map((value) => ({
  value,
  label: modalityTypesLabels[value],
}));

const durationTypeOptions = [
  { value: DurationTypes.Month, label: DurationTypesLabels.Month },
  { value: DurationTypes.Year, label: DurationTypesLabels.Year },
  { value: DurationTypes.Semester, label: DurationTypesLabels.Semester },
  { value: DurationTypes.Day, label: DurationTypesLabels.Day },
  { value: DurationTypes.Hour, label: DurationTypesLabels.Hour },
];

const formSchema = z.object({
  institution_name: z.string().trim().nonempty('Informe a instituição.'),
  course_name: z.string().trim().nonempty('Informe o curso.'),
  course_type: z.string().nonempty('Selecione uma categoria.'),
  modality: z.string().nonempty('Selecione uma modalidade.'),
  duration_unit: z.string().nonempty('Informe a duração.'),
  duration_type: z.string().nonempty('Selecione o tipo.'),
  workload: z.string(),
  link: z.string(),
  status_type: z.nativeEnum(AcademicStatusType),
});

type FormValues = z.infer<typeof formSchema>;

const FieldLabel = ({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: string;
  optional?: boolean;
}) => (
  <FormLabel htmlFor={htmlFor} sx={{ display: 'block', mb: 1 }}>
    <Typography component="span" variant="h3" color="neutral.900">
      {children}
    </Typography>
    {optional && (
      <Typography component="span" variant="body2" color="neutral.500" ml={0.5}>
        opcional
      </Typography>
    )}
  </FormLabel>
);

interface NewCourseDialogProps {
  onClose: () => void;
}

export const NewCourseDialog = ({ onClose }: NewCourseDialogProps) => {
  const { toast } = useNotification();
  const { mutate, isPending } = useCreateAcademicSituation();
  const { control, handleSubmit } = useForm({
    defaultValues: {
      institution_name: '',
      course_name: '',
      course_type: '',
      modality: '',
      duration_unit: '',
      duration_type: '',
      workload: '',
      link: '',
      status_type: AcademicStatusType.EnrollmentPending,
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit = (data: FormValues) => {
    mutate(
      {
        institution_name: data.institution_name.trim(),
        course_name: data.course_name.trim(),
        course_type: data.course_type,
        modality: data.modality,
        duration_unit: Number(data.duration_unit),
        duration_type: data.duration_type,
        workload: data.workload ? Number(data.workload) : null,
        link: data.link.trim() || null,
        status_type: data.status_type,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Curso adicionado',
            content: 'O curso foi adicionado ao seu hub acadêmico.',
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          toast({
            title: 'Erro',
            content:
              (error as AxiosError<{ errors?: string[] }>).response?.data
                ?.errors?.[0] || 'Não foi possível adicionar o curso.',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <BaseDialog
      onClose={onClose}
      open
      title={
        <Stack spacing={0.5}>
          <Typography variant="h2">Novo curso</Typography>
          <Typography variant="body1" color="neutral.600">
            Adicione um curso que foi iniciado fora da plataforma Galena.
          </Typography>
        </Stack>
      }
      actionsComponent={
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
        >
          <Button onClick={onClose} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isPending}
            variant="contained"
          >
            Salvar
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FieldLabel htmlFor="institution_name">Instituição</FieldLabel>
            <Controller
              control={control}
              name="institution_name"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="institution_name"
                  placeholder="Digite o nome da instituição"
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <FieldLabel htmlFor="course_name">Curso</FieldLabel>
            <Controller
              control={control}
              name="course_name"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="course_name"
                  placeholder="Digite o nome do curso"
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldLabel htmlFor="course_type">Categoria</FieldLabel>
            <Controller
              control={control}
              name="course_type"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="course_type"
                  select
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                  slotProps={{ select: { displayEmpty: true } }}
                >
                  <MenuItem value="" disabled>
                    Selecione uma categoria
                  </MenuItem>
                  {categoryOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldLabel htmlFor="modality">Modalidade</FieldLabel>
            <Controller
              control={control}
              name="modality"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="modality"
                  select
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                  slotProps={{ select: { displayEmpty: true } }}
                >
                  <MenuItem value="" disabled>
                    Selecione uma modalidade
                  </MenuItem>
                  {modalityOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <FieldLabel htmlFor="duration_unit">Duração</FieldLabel>
            <Controller
              control={control}
              name="duration_unit"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="duration_unit"
                  type="number"
                  placeholder="Ex.: 1, 3, 6, 10"
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FieldLabel htmlFor="duration_type">Tipo</FieldLabel>
            <Controller
              control={control}
              name="duration_type"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="duration_type"
                  select
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                  slotProps={{ select: { displayEmpty: true } }}
                >
                  <MenuItem value="" disabled>
                    Selecione
                  </MenuItem>
                  {durationTypeOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={12}>
            <FieldLabel htmlFor="workload" optional>
              Carga horária semanal
            </FieldLabel>
            <Controller
              control={control}
              name="workload"
              render={({ field }) => (
                <TextField
                  {...field}
                  id="workload"
                  type="number"
                  placeholder="Ex.: 360"
                  fullWidth
                  helperText=" "
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <FieldLabel htmlFor="link" optional>
              Link do curso
            </FieldLabel>
            <Controller
              control={control}
              name="link"
              render={({ field }) => (
                <TextField
                  {...field}
                  id="link"
                  placeholder="Ex.: link.com.br"
                  fullWidth
                  helperText=" "
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <FieldLabel htmlFor="status_type">Situação atual</FieldLabel>
            <Controller
              control={control}
              name="status_type"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="status_type"
                  select
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                >
                  {initialStatusOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </form>
    </BaseDialog>
  );
};
